import { Fragment, useCallback, useState } from "react";

import { compact, keyBy } from "lodash";
import AccordionWrapper from "react-faq-component";
import { serializeError } from "serialize-error";

import { TextInput } from "@components/v2";
import { RowVirtualizerDynamic } from "@components/virtualized-list";
import { Combobox, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { ConnectionType } from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";

export interface AdditionalAccountProps {
  extraAccountIds: Array<string>;
  updateConnection: any;
  availableAccounts: any[];
  connectedAccounts: any[];
  integrationType: ConnectionType | undefined;
  mainLoginCustomerId?: string;
}

interface ExtraAccountIds {
  extraAccountIds: Array<string>;
}

interface ExtraAccountIdsMetadata {
  metadata: ExtraAccountIds;
}

enum RequestStatus {
  NO_REQUEST,
  LOADING,
  SUCCESSFUL,
  HAS_ERROR,
}

export default function AddAdditionalSubAccount({
  extraAccountIds, // connected accounts, but not main account
  connectedAccounts,
  updateConnection,
  availableAccounts: rawAvailableAccounts,
  integrationType,
  mainLoginCustomerId,
}: AdditionalAccountProps) {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.NO_REQUEST);
  const [error, setError] = useState<string>("");
  const [selectedAccount, setSelectedAccount] = useState<Account>();
  const connectedAccountIdentfiers = new Set(
    connectedAccounts.map((account) => {
      if (integrationType === ConnectionType.integration_google_adwords) {
        return account.accountIdentifier.clientCustomerId;
      } else {
        return account.accountIdentifier;
      }
    }),
  );

  const availableAccounts = rawAvailableAccounts[0] ?? [];

  // TODO: this has horrible typing because the base API response typing is bad. Doing at least something here to provide some type safety
  const accountsById: {
    [accountId: string]: {
      data?: { login_customer_id?: string };
      name: string;
      id: string;
    };
  } = keyBy(availableAccounts, "id");

  const SubtextRow = useCallback(
    ({
      account: { accountId },
    }: {
      account: { accountName: string; accountId: string };
    }) => {
      const adWordsWarning =
        integrationType === ConnectionType.integration_google_adwords &&
        accountsById?.[accountId as string]?.data?.login_customer_id !==
          mainLoginCustomerId
          ? `You will have issues connecting this account if this account is not a child account of your parent MCC account: ${mainLoginCustomerId}`
          : "";

      return (
        <div className="text-xs text-subtitle">
          {adWordsWarning ? ` ${adWordsWarning}` : ""}
        </div>
      );
    },
    [accountsById, integrationType, mainLoginCustomerId],
  );

  const renderAccountIdCheckboxes = () => {
    const usefulIds: Account[] = compact(
      Object.values(accountsById).map(({ id, name }) =>
        connectedAccountIdentfiers.has(id)
          ? null
          : { accountName: name, accountId: id },
      ),
    );

    if (!usefulIds.length) {
      return (
        <div className="mb-5">
          <div>There are no additional accounts to add!</div>
          <div>
            If this seems wrong, please reach out to our support team:{" "}
            <a
              href="mailto:success@northbeam.io"
              target="_blank"
              rel="noreferrer"
            >
              success@northbeam.io
            </a>
          </div>
        </div>
      );
    }
    return (
      <div>
        <AccountSelectionList
          accounts={usefulIds}
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
          renderSubtext={SubtextRow}
        />
        <PrimaryButton
          onClick={() => {
            onSave(getParamsForAdditionalAccount());
          }}
          disabled={
            status === RequestStatus.LOADING || !selectedAccount?.accountId
          }
          className="w-[5rem]"
        >
          {renderButtonContent()}
        </PrimaryButton>
      </div>
    );
  };

  const onSave = async (newMetadata: ExtraAccountIdsMetadata) => {
    setStatus(RequestStatus.LOADING);
    const result = await updateConnection(newMetadata);
    if (result.errors) {
      setStatus(RequestStatus.HAS_ERROR);
      setError(JSON.stringify(serializeError(result.errors)));
    } else {
      setStatus(RequestStatus.SUCCESSFUL);
    }
  };

  const renderErrorStatus = () => {
    if (status === RequestStatus.HAS_ERROR) {
      return (
        <div className="alert alert-danger mb-3" role="alert">
          An error occurred: &ldquo;{`${JSON.stringify(error)}`}&rdquo;
        </div>
      );
    }
    return null;
  };

  const renderButtonContent = () => {
    return status === RequestStatus.LOADING ? (
      <span
        className="spinner-border spinner-border-sm"
        role="status"
        aria-hidden="true"
      ></span>
    ) : (
      "Save"
    );
  };

  const getParamsForAdditionalAccount = (): ExtraAccountIdsMetadata => {
    let updatedExtraAccountIds: Array<string>;
    if (!selectedAccount?.accountId) {
      //  this is theoretically impossible
      throw new Error("Selected account is not defined");
    }

    if (!extraAccountIds) {
      updatedExtraAccountIds = [selectedAccount.accountId];
    } else {
      updatedExtraAccountIds = [
        ...extraAccountIds,
        selectedAccount.accountId,
      ].filter((id) => id);
    }

    return {
      metadata: {
        extraAccountIds: updatedExtraAccountIds,
      },
    };
  };

  return (
    <>
      {renderErrorStatus()}
      <AccordionWrapper
        data={{
          rows: [
            {
              title: "Add another account",
              content: renderAccountIdCheckboxes(),
            },
          ],
        }}
        styles={{
          bgColor: "white",
          rowContentTextSize: "16px",
          rowContentPaddingTop: "10px",
          rowContentPaddingBottom: "10px",
          rowContentPaddingRight: "20px",
          arrowColor: "black",
        }}
        config={{ tabFocus: true }}
      />
    </>
  );
}

export type Account = {
  accountId: string;
  accountName: string;
};

function accountSelectionText({ account }: { account: Account }) {
  return `${account.accountName} (id: ${account.accountId})`;
}

export function AccountSelectionList({
  accounts,
  selectedAccount,
  setSelectedAccount,
  renderSubtext,
}: {
  accounts: Account[];
  selectedAccount?: Account;
  setSelectedAccount: (account: Account) => void;
  renderSubtext?: ({ account }: { account: Account }) => React.ReactNode;
}) {
  const [search, setSearch] = useState<string>("");

  const SelectRow = useCallback(
    ({ row: account }: { row: { accountName: string; accountId: string } }) => {
      const { accountName, accountId } = account;
      return (
        <ComboboxOption
          key={accountId}
          value={accountId}
          disabled={false}
          as={Fragment}
        >
          <label key={accountName} style={{ display: "block" }}>
            <input
              type="radio"
              checked={selectedAccount?.accountName === accountName}
              onChange={() => setSelectedAccount(account)}
              style={{ marginRight: 5 }}
            />
            {accountSelectionText({ account })}
            <div>{renderSubtext?.({ account })}</div>
          </label>
        </ComboboxOption>
      );
    },
    [renderSubtext, selectedAccount?.accountName, setSelectedAccount],
  );

  return (
    <div className="min-h-[7rem] m-1">
      <TextInput
        placeholder="Search..."
        wrapperClassName="mb-3 w-full mr-5"
        leftIcon={<i className="fa-regular fa-sharp fa-search" />}
        onChange={(e) => {
          const value = e.target.value;
          setSearch(value);
        }}
      />
      <div className="mb-3">
        <Combobox as="div">
          <ComboboxOptions static className="min-h-[15rem] overflow-auto">
            <RowVirtualizerDynamic
              rows={accounts.filter((account) => {
                const searchableText = accountSelectionText({
                  account,
                }).toLowerCase();

                return searchableText.includes(search.toLowerCase());
              })}
              Row={SelectRow}
            />
          </ComboboxOptions>
        </Combobox>
      </div>
    </div>
  );
}
